// next-app/src/providers/error-boundary.tsx
'use client';

import React, { type ReactNode } from 'react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { env } from '@/lib/env';

// Track initialization to prevent double-init
let bugsnagInitialized = false;

// Initialize Bugsnag only once and only in non-development environments
function initBugsnag() {
  if (bugsnagInitialized) return;

  // Only initialize if we have an API key and we're not in development
  if (typeof window !== 'undefined' && !env.isDevelopment && env.bugsnagApiKey) {
    Bugsnag.start({
      apiKey: env.bugsnagApiKey,
      plugins: [new BugsnagPluginReact()],
      enabledReleaseStages: ['production', 'staging'],
      releaseStage: env.environment,
      appVersion: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
    });
    bugsnagInitialized = true;
  }
}

// Initialize on module load
initBugsnag();

// Get the Bugsnag error boundary if available
function getBugsnagErrorBoundary() {
  if (bugsnagInitialized) {
    const plugin = Bugsnag.getPlugin('react');
    if (plugin) {
      return plugin.createErrorBoundary(React);
    }
  }
  return null;
}

const BugsnagErrorBoundary = getBugsnagErrorBoundary();

interface ErrorBoundaryProps {
  children: ReactNode;
}

/**
 * Error boundary component that wraps the application.
 * In non-development environments, errors are reported to Bugsnag.
 * In development, errors are only logged to the console.
 */
export function ErrorBoundary({ children }: ErrorBoundaryProps) {
  // Use Bugsnag error boundary in non-development environments
  if (BugsnagErrorBoundary) {
    return <BugsnagErrorBoundary>{children}</BugsnagErrorBoundary>;
  }

  // Passthrough in development
  return <>{children}</>;
}

// Export a manual error reporting function for use outside of React components
export function reportError(error: Error, metadata?: Record<string, unknown>): void {
  if (bugsnagInitialized && Bugsnag.isStarted()) {
    Bugsnag.notify(error, (event) => {
      if (metadata) {
        event.addMetadata('custom', metadata);
      }
    });
  } else {
    // In development or if Bugsnag isn't available, just log to console
    console.error('Error:', error, metadata);
  }
}

// Export a function to set user context
export function setErrorUser(user: { id?: string; email?: string; name?: string }): void {
  if (bugsnagInitialized && Bugsnag.isStarted()) {
    Bugsnag.setUser(user.id, user.email, user.name);
  }
}

// Export a function to clear user context (e.g., on logout)
export function clearErrorUser(): void {
  if (bugsnagInitialized && Bugsnag.isStarted()) {
    Bugsnag.setUser(undefined, undefined, undefined);
  }
}
